'use client';

/**
 * `useJourneyMetronomeBridge`, forwards `ContinuousMetronome` beats into
 * the `LearningJourney`'s performance capture.
 *
 * The `ContinuousMetronomeBeatEvent` exposes `beat` (0-based), `measure`,
 * `audioTime` and `realTimeMs`, but no song-position tick: the metronome
 * is a free-running scheduler decoupled from the transport. The journey
 * detectors however reason in ticks, so we resolve the tick at beat time
 * by reading `LocalTransport.currentTick()` from inside the listener.
 *
 * Why that's correct
 * ------------------
 * The ContinuousMetronome listener fires up to ~100 ms before the audible
 * click (audio-clock lookahead window). `realTimeMs` accounts for this
 * offset. The `currentTick()` reading at listener time, on the other hand,
 * is the *playhead* position right now, not at the click time. The two
 * are not perfectly aligned, but they are both monotonically increasing
 * and the inaccuracy is bounded by the lookahead. For V1 detector use
 * (~50 ms rhythmic windows, beat-grid sanity checks) this is acceptable.
 * Tightening the alignment is tracked as a follow-up; a clean fix would
 * be to expose a "tickAt(realTimeMs)" helper on the transport.
 *
 * `beatInBar` is exposed 1-based (downbeat = 1) per the `RecordedBeat`
 * contract (cf. `lib/learning-journey/types.ts`); the metronome event is
 * 0-based, hence the `+ 1`.
 *
 * Pure side effect, no return value. Cleanup unsubscribes the listener.
 */

import { useEffect } from 'react';
import type { LearningJourney } from '@/lib/learning-journey/journey';
import type { Studio } from '@/lib/studio/studio';

export function useJourneyMetronomeBridge(
  journey: LearningJourney | null,
  studio: Studio | null,
): void {
  useEffect(() => {
    if (journey === null || studio === null) {
      return;
    }

    const metronome = studio.continuousMetronome;
    const transport = studio.getLocalTransport();

    const unsubscribe = metronome.onBeat((event) => {
      const bpm = metronome.bpm;
      const beatDurationMs = bpm > 0 ? 60_000 / bpm : 0;

      journey.recordBeat({
        tick: transport.currentTick(),
        beatInBar: event.beat + 1,
        realTimeMs: event.realTimeMs,
        beatDurationMs,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [journey, studio]);
}
